import {createStyles, Stack, Table} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useOnMount from 'ui-library/hooks/useOnMount';
import Menu from 'ui-library/menus/Menu';
import type {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

/** Sentinel value representing a column the user chose to ignore (no mapping). */
export const IGNORE_FIELD_ID = '__ignore__';

/**
 * Describes a single field available for column mapping.
 */
export interface MappableField {
  /** Unique identifier for this field (e.g. apiId or a synthetic key like `__shared__Name`). */
  id: string;
  /** Human-readable label shown in the dropdown. */
  displayName: string;
  /** If set, places this field under a collapsible group in the dropdown. */
  group?: string;
  /** When true, the field label is appended with an asterisk to indicate required. */
  isRequired?: boolean;
}

/**
 * Props for the ColumnMapper component.
 */
export interface ColumnMapperProps {
  /** Column headers parsed from the uploaded file. */
  headers: Array<string>;
  /** Fields available for mapping. */
  fields: Array<MappableField>;
  /** Current column→field mappings (header name → field id or IGNORE_FIELD_ID for Ignore). */
  mappings: Record<string, string>;
  /** Called with the full updated mappings object whenever a mapping changes. */
  onMappingChange: (mappings: Record<string, string>) => void;
  /** When true, runs auto-detection on mount to pre-fill mappings. */
  autoDetect?: boolean;
  /** Custom auto-detection function. A future generic default could match display names case-insensitively. */
  autoDetectFn?: (header: string, fields: Array<MappableField>) => string | null;
  /** When provided, renders a "Reset to default" button below the table. Called when clicked. */
  onReset?: () => void;
}

export function columnLetter(index: number): string {
  let result = '';
  let n = index;
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

function buildOptionDatas(fields: Array<MappableField>, usedFieldIds: Set<string>): Array<OptionData> {
  const flatFields = fields.filter((f) => !f.group);
  const grouped: Record<string, Array<MappableField>> = {};
  for (const field of fields) {
    if (field.group) {
      if (!grouped[field.group]) {
        grouped[field.group] = [];
      }
      grouped[field.group].push(field);
    }
  }

  const options: Array<OptionData> = [];

  for (const field of flatFields) {
    options.push({
      name: field.isRequired ? `${field.displayName} *` : field.displayName,
      value: field.id,
      pureTextName: field.displayName,
      disabled: usedFieldIds.has(field.id),
    });
  }

  for (const [groupName, groupFields] of Object.entries(grouped)) {
    options.push({
      name: groupName,
      value: groupName,
      submenu: [
        groupFields.map((field) => ({
          name: field.isRequired ? `${field.displayName} *` : field.displayName,
          value: field.id,
          pureTextName: field.displayName,
          disabled: usedFieldIds.has(field.id),
        })),
      ],
    });
  }

  options.push({
    name: 'Ignore',
    value: IGNORE_FIELD_ID,
    pureTextName: 'Ignore',
  });

  return options;
}

const useStyles = createStyles((theme) => ({
  table: {
    '& thead th': {
      fontWeight: 600,
      fontSize: theme.fontSizes.md,
      padding: '8px',
    },
    '& tbody td': {
      padding: '6px 8px',
      fontSize: theme.fontSizes.md,
      verticalAlign: 'middle',
    },
  },
  columnLetterCell: {
    width: 60,
  },
  columnNameCell: {
    minWidth: 150,
  },
}));

function getDisplayName(fields: Array<MappableField>, fieldId: string | undefined): string {
  if (fieldId == null) {
    return 'Select field...';
  }
  if (fieldId === IGNORE_FIELD_ID) {
    return 'Ignore';
  }
  const field = fields.find((f) => f.id === fieldId);
  return field?.displayName ?? 'Select field...';
}

/**
 * A reusable column-to-field mapper table.
 *
 * Renders a table of spreadsheet column headers with dropdowns allowing the user
 * to map each column to a schema field, or mark it as "Ignore". Supports optional
 * auto-detection of mappings on mount via a caller-supplied function.
 */
export default function ColumnMapper({
  headers,
  fields,
  mappings,
  onMappingChange,
  autoDetect,
  autoDetectFn,
  onReset,
}: ColumnMapperProps): JSX.Element {
  const {classes} = useStyles();

  useOnMount(() => {
    if (!autoDetect) {
      return;
    }
    assert(autoDetectFn != null, '`autoDetectFn` must be provided when `autoDetect` is true.');
    if (Object.keys(mappings).length > 0) {
      return;
    }
    const detected: Record<string, string> = {};
    const usedIds = new Set<string>();
    for (const header of headers) {
      const fieldId = autoDetectFn(header, fields);
      if (fieldId != null && !usedIds.has(fieldId)) {
        detected[header] = fieldId;
        usedIds.add(fieldId);
      }
    }
    if (Object.keys(detected).length > 0) {
      onMappingChange(detected);
    }
  });

  const handleFieldChange = useCallback(
    (header: string, value: string) => {
      onMappingChange({...mappings, [header]: value});
    },
    [mappings, onMappingChange]
  );

  const optionDatasByHeader = useMemo(
    () =>
      Object.fromEntries(
        headers.map((header) => {
          const usedFieldIds = new Set(
            Object.entries(mappings)
              .filter(([h, v]) => h !== header && v && v !== IGNORE_FIELD_ID)
              .map(([, v]) => v)
          );
          return [header, buildOptionDatas(fields, usedFieldIds)];
        })
      ),
    [headers, fields, mappings]
  );

  return (
    <Stack spacing="sm">
      <Table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.columnLetterCell}>Column</th>
            <th className={classes.columnNameCell}>Column Name</th>
            <th>Column Type</th>
          </tr>
        </thead>
        <tbody>
          {headers.map((header, index) => (
            <tr key={header}>
              <td className={classes.columnLetterCell}>{columnLetter(index)}</td>
              <td className={classes.columnNameCell}>{header}</td>
              <td>
                <SelectDropdown
                  displayName={getDisplayName(fields, mappings[header])}
                  aria-label={`Map column "${header}" to a field`}
                  menu={
                    <SearchableMenu
                      onSelect={(value: string) => {
                        handleFieldChange(header, value);
                      }}
                      optionDatas={optionDatasByHeader[header]}
                      selectedValue={mappings[header] ?? null}
                      width={Menu.WIDTHS.MEDIUM}
                    />
                  }
                  closeOnInsideClick={true}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {onReset && (
        <Button buttonStyle={ButtonStyle.LINK} onClick={onReset}>
          Reset to default
        </Button>
      )}
    </Stack>
  );
}
